/**
 * usePollingConfig Hook
 *
 * Provides the resolved polling configuration by merging:
 * 1. GrowthBook preset-specific overrides (highest priority)
 * 2. GrowthBook global overrides
 * 3. Hardcoded preset defaults
 * 4. Environment variable REACT_APP_POLLING_INTERVAL_MS (for DEFAULT preset)
 * 5. Hardcoded base defaults (lowest priority)
 *
 * Usage:
 * ```tsx
 * // Use default preset
 * const config = usePollingConfig();
 *
 * // Use specific preset for different polling scenarios
 * const logsConfig = usePollingConfig('FLOW_LOGS');      // 10s, real-time logs
 * const samplesConfig = usePollingConfig('LIVE_SAMPLES'); // 20s, data arrival
 * const listConfig = usePollingConfig('PIPELINE_LIST');   // 30s, dashboard
 * const uiConfig = usePollingConfig('UI_TIME_DISPLAY');   // 10s, client-side
 * const scanConfig = usePollingConfig('SCANNING');        // 5s, detection
 * ```
 *
 * GrowthBook Configuration Example (ui:polling-config):
 * ```json
 * {
 *   "intervalMs": null,
 *   "pauseOnHidden": true,
 *   "presets": {
 *     "FLOW_LOGS": { "intervalMs": 15000 },
 *     "PIPELINE_LIST": { "intervalMs": 45000, "pauseOnIdle": true },
 *     "SCANNING": { "intervalMs": 3000 }
 *   }
 * }
 * ```
 */

import { useMemo } from 'react';
import { useFeatureValue } from 'new/hooks/useFeatureFlags';
import { PollingConfig } from 'new/constants/featureFlags';
import {
  mergePollingConfig,
  ResolvedPollingConfig,
  PollingPreset,
} from 'new/constants/pollingConfig';

// Re-export types for convenience
export type { PollingPreset, ResolvedPollingConfig };

/**
 * Hook to get the resolved polling configuration for a specific preset
 * Automatically merges GrowthBook overrides with preset/environment/default values
 *
 * @param preset - The polling preset to use (defaults to 'DEFAULT')
 * @returns ResolvedPollingConfig - The merged polling configuration
 */
export function usePollingConfig(preset: PollingPreset = 'DEFAULT'): ResolvedPollingConfig {
  // Get GrowthBook override (if any)
  const gbConfig = useFeatureValue<PollingConfig>('UI_POLLING_CONFIG');

  // Merge: GrowthBook overrides -> preset defaults -> env variable -> hardcoded defaults
  const resolvedConfig = useMemo(() => mergePollingConfig(gbConfig, preset), [gbConfig, preset]);

  return resolvedConfig;
}

export default usePollingConfig;
